import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
	Box,
	Card,
	Divider,
	Group,
	SkeletonRect,
	Stack,
	Tag,
	Text,
} from '@ekhodealer/ekho-common/rev';

import { formatNumberWithCommas } from '@common/lib/currency';

import { isGeneratingOrdersReportAtom,ordersReportDataAtom } from '../../atoms/ordersAtoms';

import './styles.css';

export default function ReportsOrdersSummary() {
	const reportData = useAtomValue(ordersReportDataAtom);
	const isPending = useAtomValue(isGeneratingOrdersReportAtom);

	const summary = useMemo(() => {
		if (!reportData || reportData.length === 0) {
			return {
				totalOrders: 0,
				unfulfilledOrders: 0,
				fulfilledOrders: 0,
				canceledOrders: 0,
				onHoldOrders: 0,
				pendingOrders: 0,
				amountPaidToDate: 0,
				amountOutstanding: 0,
				totalPaidAndOwed: 0,
			};
		}

		// Helper to parse value to number
		const parseToNumber = (value: number | string | undefined | null): number => {
			if (value === null || value === undefined) return 0;
			if (typeof value === 'number') return value;
			if (typeof value === 'string') {
				const parsed = parseFloat(value);
				return isNaN(parsed) ? 0 : parsed;
			}
			return 0;
		};

		const totals = reportData.reduce(
			(acc, item) => {
				const totalDue = parseToNumber(item.totalDue);
				const totalPaid = parseToNumber(item.totalPaid);
				const outstanding = totalDue - totalPaid;

				acc.totalOrders += 1;
				acc.amountPaidToDate += totalPaid;
				acc.totalPaidAndOwed += totalDue;

				// Calculate outstanding amount (only positive values)
				if (outstanding > 0) {
					acc.amountOutstanding += outstanding;
				}

				// Map vehicleFulfillmentStatus directly to submetrics
				const fulfillmentStatus = (item.vehicleFulfillmentStatus || '').toUpperCase();

				if (fulfillmentStatus === 'UNFULFILLED') {
					acc.unfulfilledOrders += 1;
				} else if (fulfillmentStatus === 'FULFILLED') {
					acc.fulfilledOrders += 1;
				} else if (fulfillmentStatus === 'CANCELED') {
					acc.canceledOrders += 1;
				} else if (fulfillmentStatus === 'ON_HOLD') {
					acc.onHoldOrders += 1;
				} else {
					// Pending: anything not in the above buckets
					acc.pendingOrders += 1;
				}

				return acc;
			},
			{
				totalOrders: 0,
				unfulfilledOrders: 0,
				fulfilledOrders: 0,
				canceledOrders: 0,
				onHoldOrders: 0,
				pendingOrders: 0,
				amountPaidToDate: 0,
				amountOutstanding: 0,
				totalPaidAndOwed: 0,
			},
		);

		return totals;
	}, [reportData]);

	// Show skeleton when loading
	if (isPending) {
		return (
			<div className='summary-container'>
				<div className='summary-item'>
					<div className='summary-card'>
						<div className='metrics-card-content'>
							<div className='main-metric-group'>
								<SkeletonRect width={140} height={27} rounding='8' />
								<SkeletonRect width={80} height={17} rounding='8' />
							</div>
							<div className='sub-metrics-container sub-metrics-vertical'>
								<div className='sub-metric-item sub-metric-item-full-width'>
									<SkeletonRect width={60} height={17} rounding='8' />
									<SkeletonRect width={100} height={19} rounding='8' />
								</div>
								<div className='sub-metric-item sub-metric-item-full-width'>
									<SkeletonRect width={90} height={17} rounding='8' />
									<SkeletonRect width={100} height={19} rounding='8' />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='summary-item'>
					<div className='summary-card'>
						<div className='metrics-card-content'>
							<div className='main-metric-group'>
								<SkeletonRect width={40} height={27} rounding='8' />
								<SkeletonRect width={100} height={17} rounding='8' />
							</div>
							<div className='sub-metrics-grid'>
								{[1, 2, 3, 4, 5].map((index) => (
									<div key={index} className='sub-metric-grid-item'>
										<div className='sub-metric-content'>
											<SkeletonRect
												width={
													index === 3 || index === 4 || index === 5
														? 100
														: 80
												}
												height={24}
												rounding='8'
											/>
											<SkeletonRect width={30} height={19} rounding='8' />
										</div>
										<div className='sub-metric-divider'>
											<Divider />
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Don't show summary if no report has been generated yet, or if report is empty
	if (reportData === null || reportData.length === 0) {
		return null;
	}

	return (
		<div className='summary-container'>
			<Card className='p-0'>
				<Box padding={20} className='w-full'>
					<Stack itemsSpacing={20} itemsAlignX='stretch'>
						<Stack itemsSpacing={4} itemsAlignX='stretch'>
							<Text size='title100' weight='bold' align='left'>
								${formatNumberWithCommas(summary.totalPaidAndOwed.toFixed(2))}
							</Text>
							<Text size='footnote' weight='semibold' color='secondary' align='left'>
								Total value
							</Text>
						</Stack>
						<Stack itemsSpacing={8}>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Paid
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											$
											{formatNumberWithCommas(
												summary.amountPaidToDate.toFixed(2),
											)}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Outstanding
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											$
											{formatNumberWithCommas(
												summary.amountOutstanding.toFixed(2),
											)}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
						</Stack>
					</Stack>
				</Box>
			</Card>
			<Card className='p-0'>
				<Box padding={20} className='w-full'>
					<Stack itemsSpacing={20} itemsAlignX='stretch'>
						<Stack itemsSpacing={4} itemsAlignX='stretch'>
							<Text size='title100' weight='bold' align='left'>
								{summary.totalOrders}
							</Text>
							<Text size='footnote' weight='semibold' color='secondary' align='left'>
								Total orders
							</Text>
						</Stack>
						<div className='sub-metrics-grid'>
							<Stack itemsSpacing={0} itemsAlignX='stretch'>
								<Group padding={8}>
									<Tag appearance='outline' accent={3}>
										<Text size='bodySmall' weight='semibold'>
											Fulfilled
										</Text>
									</Tag>
									<div>{summary.fulfilledOrders}</div>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0} itemsAlignX='stretch'>
								<Group padding={8}>
									<Tag appearance='outline' accent={2}>
										<Text size='bodySmall' weight='semibold'>
											Pending
										</Text>
									</Tag>
									<div>{summary.pendingOrders}</div>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0} itemsAlignX='stretch'>
								<Group padding={8}>
									<Tag appearance='outline' accent={1}>
										<Text size='bodySmall' weight='semibold'>
											Unfulfilled
										</Text>
									</Tag>
									<div>{summary.unfulfilledOrders}</div>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0} itemsAlignX='stretch'>
								<Group padding={8}>
									<Tag appearance='outline' accent={5}>
										<Text size='bodySmall' weight='semibold'>
											On Hold
										</Text>
									</Tag>
									<div>{summary.onHoldOrders}</div>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0} itemsAlignX='stretch'>
								<Group padding={8}>
									<Tag appearance='outline' accent={'neutral'}>
										<Text size='bodySmall' weight='semibold'>
											Cancelled
										</Text>
									</Tag>
									<div>{summary.canceledOrders}</div>
								</Group>
								<Divider />
							</Stack>
						</div>
					</Stack>
				</Box>
			</Card>
		</div>
	);
}
